import { forwardRef, useEffect, useMemo, useState } from "react"
import { Building2, FileText, Info, MapPin, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { FlightOptionView } from "@/types/booking"
import { useBooking } from "@/context/BookingContext"
import { resolveAirportCode } from "@/lib/airports"
import { getNightCount, getSelectedStayPeriod } from "@/lib/booking"
import { FlightCard } from "./FlightCard"
import { SectionHeader } from "./SectionHeader"
import { TRexLoader } from "./TRexLoader"

function useFlightProgress(isLoading: boolean) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!isLoading) {
      setValue(0)
      return
    }
    let raf = 0
    setValue(0)
    const tick = () => {
      setValue((cur) => cur + (85 - cur) * 0.015)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [isLoading])

  return Math.round(value)
}

interface FlightSectionProps {
  onFlightSelect: (flight: FlightOptionView) => void
  onEditAirport?: () => void
  onHotelOnly?: () => void
  onCustomForm?: () => void
  isFlightOnly?: boolean
}

export const FlightSection = forwardRef<HTMLElement, FlightSectionProps>(
  function FlightSection(
    { onFlightSelect, onEditAirport, onHotelOnly, onCustomForm, isFlightOnly },
    ref
  ) {
    const { state } = useBooking()
    const [airportLabels, setAirportLabels] = useState<Record<string, string>>({})
    const selectedStayPeriod = getSelectedStayPeriod(state)
    const nightCount = getNightCount(
      selectedStayPeriod?.stayStartDate,
      selectedStayPeriod?.stayEndDate
    )
    const sortedFlights = useMemo(() => {
      const flights = state.flightOptions
      return [...flights].sort((a, b) => {
        if (a.priceDiffPerPerson === 0 && b.priceDiffPerPerson !== 0) return -1
        if (a.priceDiffPerPerson !== 0 && b.priceDiffPerPerson === 0) return 1
        return 0
      })
    }, [state.flightOptions])
    const destinationCodes = useMemo(() => {
      const codes = new Set<string>()

      sortedFlights.forEach((flight) => {
        if (flight.outbound.destinationAirportCode) {
          codes.add(flight.outbound.destinationAirportCode)
        }
      })

      if (state.event?.airport) {
        codes.add(state.event.airport)
      }

      return [...codes]
    }, [sortedFlights, state.event?.airport])

    useEffect(() => {
      let cancelled = false

      async function loadAirportLabels() {
        const entries = await Promise.all(
          destinationCodes.map(async (code) => {
            const resolved = await resolveAirportCode(code)
            return [code, resolved?.city ?? code] as const
          })
        )

        if (cancelled) return

        setAirportLabels((current) => {
          const next = { ...current }
          let changed = false

          entries.forEach(([code, label]) => {
            if (next[code] !== label) {
              next[code] = label
              changed = true
            }
          })

          return changed ? next : current
        })
      }

      if (destinationCodes.length > 0) {
        void loadAirportLabels()
      }

      return () => {
        cancelled = true
      }
    }, [destinationCodes])

    const eventDestinationLabel = state.event?.airport
      ? (airportLabels[state.event.airport] ?? state.event.airport)
      : "Event destination"
    const isLoading = state.flightOffersStatus === "loading"
    const hasError = state.flightOffersStatus === "error"
    const hasNoFlights = !isLoading && !hasError && sortedFlights.length === 0
    const flightProgress = useFlightProgress(isLoading)

    return (
      <section ref={ref} className="scroll-mt-6 py-10">
        <SectionHeader
          step={isFlightOnly ? 1 : 2}
          title="Choose Your Flight"
          subtitle="Live flight options for your selected departure airports"
        />

        {isLoading ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card/80 px-6 py-12 text-center">
            <TRexLoader variant="inline" value={flightProgress} trexTrigger="loop" />
          </div>
        ) : hasError ? (
          <div
            className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card/80 px-6 py-12 text-center"
          >
            <AlertTriangle className="size-8 text-amber-400" />
            <p className="text-sm text-muted-foreground">
              {state.flightOffersError?.userMessage ?? "Could not load flights."}{!isFlightOnly && " You can still book hotel-only."}
            </p>
            {!isFlightOnly && (
              <div className="flex w-full max-w-md flex-col gap-3">
                <Button variant="outline" className="w-full gap-2 py-3.5" onClick={onHotelOnly}>
                  <Building2 className="size-4" />
                  Continue with Hotel Only
                </Button>
              </div>
            )}
          </div>
        ) : hasNoFlights ? (
          <div
            className="flex flex-col items-center gap-6 rounded-xl border border-border bg-card/80 px-6 py-12 text-center"
          >
            <div className="flex size-16 items-center justify-center rounded-full bg-secondary">
              <svg
                width="28"
                height="28"
                viewBox="0 0 13.3333 13.0994"
                fill="none"
              >
                <path
                  d="M5.33333 7.86667L1.11667 9.55C0.85 9.66111 0.597333 9.63622 0.358667 9.47533C0.12 9.31444 0.000444444 9.08933 0 8.8V8.43333C0 8.3 0.0306667 8.17222 0.092 8.05C0.153333 7.92778 0.239333 7.82778 0.35 7.75L5.33333 4.26667V1.33333C5.33333 0.966668 5.464 0.65289 5.72533 0.392001C5.98667 0.131112 6.30044 0.000445574 6.66667 1.12994e-06C7.03289 -0.000443314 7.34689 0.130223 7.60867 0.392001C7.87044 0.653779 8.00089 0.967557 8 1.33333V4.26667L12.9833 7.75C13.0944 7.82778 13.1807 7.92778 13.242 8.05C13.3033 8.17222 13.3338 8.3 13.3333 8.43333V8.8C13.3333 9.08889 13.214 9.314 12.9753 9.47533C12.7367 9.63667 12.4838 9.66156 12.2167 9.55L8 7.86667V10.2667L9.71667 11.4667C9.80556 11.5333 9.87511 11.614 9.92533 11.7087C9.97556 11.8033 10.0004 11.906 10 12.0167V12.4167C10 12.6389 9.90822 12.8196 9.72467 12.9587C9.54111 13.0978 9.33844 13.1338 9.11667 13.0667L6.66667 12.3333L4.21667 13.0667C3.99444 13.1333 3.79156 13.0973 3.608 12.9587C3.42444 12.82 3.33289 12.6393 3.33333 12.4167V12.0167C3.33333 11.9056 3.35844 11.8029 3.40867 11.7087C3.45889 11.6144 3.52822 11.5338 3.61667 11.4667L5.33333 10.2667V7.86667Z"
                  fill="currentColor"
                  className="text-muted-foreground"
                />
              </svg>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground">
                No Flights Available
              </h3>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                We could not find flights for the selected departure airport(s)
                and dates.
              </p>
            </div>

            <div className="flex w-full max-w-md flex-col gap-3">
              <Button className="w-full gap-2 py-3.5 font-bold" onClick={onEditAirport}>
                <MapPin className="size-4" />
                Edit Departure Airport
              </Button>

              {!isFlightOnly && (
                <Button variant="outline" className="w-full gap-2 py-3.5" onClick={onHotelOnly}>
                  <Building2 className="size-4" />
                  Continue with Hotel Only
                </Button>
              )}

              <Button variant="outline" className="w-full gap-2 py-3.5" onClick={onCustomForm}>
                <FileText className="size-4" />
                Fill Booking Request
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {sortedFlights.map((flight) => (
              <FlightCard
                key={flight.selectionId}
                flight={flight}
                visuallySelected={
                  state.userSelectedFlight &&
                  state.selectedFlight?.selectionId === flight.selectionId
                }
                isIncludedInPackage={
                  flight.priceDiffPerPerson === 0 &&
                  state.selectedFlight?.selectionId === flight.selectionId &&
                  !state.userSelectedFlight
                }
                currency={state.currency}
                destinationLabel={
                  airportLabels[flight.outbound.destinationAirportCode] ??
                  eventDestinationLabel
                }
                selectedPriceDiffPerPerson={
                  state.selectedFlight?.priceDiffPerPerson ?? 0
                }
                nightCount={nightCount}
                onSelect={() => onFlightSelect(flight)}
              />
            ))}

            <div className="flex items-center gap-2">
              <div className="flex size-4 items-center justify-center rounded-full bg-amber-400/15">
                <Info className="size-2.5 text-amber-400" />
              </div>
              <span className="text-xs text-muted-foreground">
                Flight price is valid at this moment and may change before booking is completed
              </span>
            </div>
          </div>
        )}
      </section>
    )
  }
)
